import { Component, Input, OnDestroy } from '@angular/core';
import { GeminiImageService } from '../../services/gemini-image.service';

interface EditedImage {
  id: string;
  originalUrl: string;
  editedUrl: string;
  prompt: string;
  timestamp: number;
}

interface ProcessingState {
  isProcessing: boolean;
  error: string | null;
}

@Component({
  selector: 'app-ie',
  templateUrl: './ie.component.html',
  styleUrls: ['./ie.component.scss']
})
export class IEComponent implements OnDestroy {
  @Input() showHeader = true;
  @Input() embedded = false;
  @Input() showMobileFooter = true;

  selectedFile: File | null = null;
  selectedFileAccepted = false;
  previewUrl: string | null = null;
  prompt = '';
  history: EditedImage[] = [];
  processing: ProcessingState = { isProcessing: false, error: null };
  currentResult: string | null = null;

  private readonly acceptedImageTypes = new Set<string>([
    'image/png',
    'image/jpeg',
    'image/jpg',
    'image/webp'
  ]);

  constructor(private geminiImage: GeminiImageService) {}

  ngOnDestroy(): void {
    this.revokePreviewUrl();
  }

  onFileChange(event: Event): void {
    const input = event.target as HTMLInputElement;
    const file = input?.files?.[0];
    if (!file) return;

    if (!this.isAcceptedImageType(file)) {
      this.selectedFile = null;
      this.selectedFileAccepted = false;
      this.processing = { isProcessing: false, error: 'Accepted image types: PNG, JPG, JPEG, or WebP.' };
      return;
    }

    if (file.size > 10 * 1024 * 1024) {
      this.selectedFile = null;
      this.selectedFileAccepted = false;
      this.processing = { isProcessing: false, error: 'PNG, JPG or WebP only (max 10MB).' };
      return;
    }

    this.selectedFile = file;
    this.selectedFileAccepted = true;
    this.setPreviewUrl(URL.createObjectURL(file));
    this.currentResult = null;
    this.processing = { isProcessing: false, error: null };
  }

  async handleEdit(): Promise<void> {
    if (!this.canSubmit) return;

    this.processing = { isProcessing: true, error: null };

    try {
      const base64 = await this.fileToBase64(this.selectedFile);
      const editedBase64 = await this.geminiImage.editImage(
        base64,
        this.selectedFile.type || 'image/png',
        this.prompt.trim()
      );

      this.currentResult = editedBase64;

      const newEntry: EditedImage = {
        id: crypto.randomUUID(),
        originalUrl: this.previewUrl || '',
        editedUrl: editedBase64,
        prompt: this.prompt,
        timestamp: Date.now()
      };

      this.history = [newEntry, ...this.history];
    } catch (err: any) {
      this.processing = { isProcessing: false, error: err?.message || 'Failed to edit image.' };
      return;
    } finally {
      this.processing = { ...this.processing, isProcessing: false };
    }
  }

  clearSession(): void {
    this.selectedFile = null;
    this.selectedFileAccepted = false;
    this.currentResult = null;
    this.prompt = '';
    this.processing = { isProcessing: false, error: null };
    this.setPreviewUrl(null);
  }

  get canSubmit(): boolean {
    return !!this.prompt.trim() && !!this.selectedFile && this.selectedFileAccepted && !this.processing.isProcessing;
  }

  deleteHistoryItem(id: string): void {
    this.history = this.history.filter((item) => item.id !== id);
  }

  viewHistoryItem(url: string): void {
    this.currentResult = url;
  }

  private fileToBase64(file: File): Promise<string> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => resolve(reader.result as string);
      reader.onerror = (error) => reject(error);
    });
  }

  private setPreviewUrl(url: string | null): void {
    if (this.previewUrl && this.previewUrl.startsWith('blob:')) {
      URL.revokeObjectURL(this.previewUrl);
    }
    this.previewUrl = url;
  }

  private revokePreviewUrl(): void {
    if (this.previewUrl && this.previewUrl.startsWith('blob:')) {
      URL.revokeObjectURL(this.previewUrl);
    }
  }

  private isAcceptedImageType(file: File): boolean {
    return this.acceptedImageTypes.has((file.type || '').toLowerCase());
  }
}
